import React from 'react';

import {
  Avatar,
  Button,
  ClickableComponent,
  Flexbox,
  FontAwesomeIcon,
  IconButton,
  Text,
} from '@schoolinks/ui-components';
import cn from 'classnames';

import { find, isEmpty, isEqual, noop } from 'lodash';
import { bool, func, shape, string, arrayOf } from 'prop-types';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion';
import { faFile } from '@fortawesome/pro-regular-svg-icons/faFile';
import { faWallet } from '@fortawesome/pro-regular-svg-icons/faWallet';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons/faEllipsisVertical';

import Popover from '@mui/material/Popover';

import * as googleTranslateActions from 'actions/googleTranslate';
import removeJwt from 'actions/removeJwt';

import GoogleTranslate from 'components/Common/GoogleTranslateV2';
import Wrapper from 'components/Common/Layout/components/Wrapper';
import LanguageTranslationMenuItem from 'components/Common/LayoutV2/AppBar/components/LanguageTranslationMenuItem';
import AppbarLink from 'components/Common/LayoutV2/AppBar/DesktopV2/components/AppbarLink';
import Item from 'components/Common/LayoutV2/AppBar/DesktopV2/components/Item';
import Settings from 'components/Common/LayoutV2/AppBar/DesktopV2/components/Settings';
import LogoutButton from 'components/Common/LayoutV2/AppBar/LogoutButton';
import PageDetailsContainer from 'components/Common/LayoutV2/AppBar/PageDetails/PageDetailsContainer';
import TextToSpeech from 'components/Common/LayoutV2/AppBar/TextToSpeech';
import { APP_BAR_ITEM_ORDER } from 'components/Common/LayoutV2/constants';
import {
  getRelevantNavItems,
  isElementaryPage,
} from 'components/Common/LayoutV2/utils';
import Chat from 'components/CommonV2/Icons/Chat';
import LogoutIcon from 'components/CommonV2/Icons/Logout';

import { SUPPORT_URL } from 'config_app';
import { USER_TYPES } from 'constants/userTypes';

import withRouter from 'containers/withRouter';
import withUserType from 'containers/withUserType';

import withMediaInfo from 'HOC/withMediaInfo';
import withModal from 'HOC/withModal';

import ModalPropTypes, { ModalDefaultProps } from 'propTypes/ModalPropTypes';
import { RouterPropTypes } from 'propTypes/RouterPropTypes';
import {
  DOCUMENT_CENTER,
  LOGOUT,
  EL_STUDENT_DASHBOARD,
  EL_STUDENT_CAREER_QUIZ,
  LOGIN_K12,
  ONBOARDING,
  ROOT,
  CREDENTIAL_WALLET,
} from 'routes/constants';

import { ensureArray } from 'utils/arrays';

import getFullName from 'utils/getFullName';

import K12DashboardSwitcher from 'modules/AccountSetupV2/widgets/K12DashboardSwitcher';
import Notifications from 'modules/Notifications';
import Messages from 'modules/Notifications/Messages';
import StudentDemoAccount from 'modules/StudentDemoAccount';
import WelcomeWidget from 'modules/WelcomeWidget';

import s from './DesktopAppBarV2.css';

@connect(
  ({
    googleTranslate: { showGoogleTranslate },
    user: {
      profilePic,
      email,
      firstName,
      lastName,
      preferredName,
      moduleAccess,
      featureOnboarding,
      layout = {},
      activeUserType = [],
      currentUserMode,
    } = {},
    modals: { modalType } = {},
  }) => ({
    profilePic,
    email,
    firstName,
    lastName,
    preferredName,
    showGoogleTranslate,
    moduleAccess,
    featureOnboarding,
    appBar: getRelevantNavItems(
      layout,
      activeUserType,
      'appBar',
      currentUserMode,
    ),
    shouldRenderAffiliationLink:
      !activeUserType?.includes('member') &&
      activeUserType?.includes('guardian'),
    isModalVisible: Boolean(modalType),
  }),
  d => bindActionCreators(googleTranslateActions, d),
)
@withTranslation()
@withModal
@withMediaInfo()
@withUserType
@withRouter
class DesktopAppBarV2 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      elementaryMobileMenuAnchor: null,
    };
  }

  shouldComponentUpdate(nextProps, nextStates) {
    return !isEqual(nextProps, this.props) || !isEqual(nextStates, this.state);
  }

  clearGuestOrganizationCache = () => {
    removeJwt();
    localStorage.removeItem('guestUserRedirectedEvent');
    localStorage.removeItem('guestUserFormValues');
  };

  renderWelcomeWidget = () => {
    const {
      user: { isCounselor },
      isOnboarding,
    } = this.props;

    if (isOnboarding) return null;

    return isCounselor ? (
      <WelcomeWidget key="internal-helper" />
    ) : (
      <AppbarLink
        aria-label="Get help with SchooLinks"
        icon={faCircleQuestion}
        href={SUPPORT_URL}
        tooltip="Need help?"
      />
    );
  };

  renderAvatar = (config = {}) => {
    const { withName, editProfile } = config;
    const { profilePic, firstName, lastName, isOnboarding, preferredName } =
      this.props;

    if (isOnboarding) return null;

    const fullName = getFullName({ firstName, lastName, preferredName });
    const initials =
      firstName && lastName ? `${firstName[0]}${lastName[0]}` : '';

    return (
      <NavLink
        aria-label="Edit my profile"
        to={editProfile ? '/profile/edit' : '/account-settings/profile-photo'}
        className={s.avatarLink}
      >
        <Flexbox alignItems="center" gap={1} px={1}>
          {initials ? (
            <Avatar size="sm" src={profilePic}>
              {initials}
            </Avatar>
          ) : (
            <Avatar size="sm" src={profilePic} />
          )}
          {withName && (
            <Text noWrap variant="bodySm600">
              {fullName}
            </Text>
          )}
        </Flexbox>
      </NavLink>
    );
  };

  renderDemoStudentButton = item => (
    <StudentDemoAccount k12AdminTooltip={item?.tooltip} key="student-demo" />
  );

  renderCredentialWalletButton = () => {
    const { isOnboarding } = this.props;
    if (isOnboarding) return null;

    return (
      <AppbarLink
        icon={faWallet}
        href={CREDENTIAL_WALLET}
        aria-label="Go to credential wallet"
      />
    );
  };

  renderDocumentsButton = () => {
    const { isOnboarding } = this.props;
    if (isOnboarding) return null;

    return (
      <AppbarLink
        icon={faFile}
        href={DOCUMENT_CENTER}
        aria-label="Go to document center"
      />
    );
  };

  renderGoogleTranslate = ({ isElementary }) => {
    const {
      showGoogleTranslate,
      toggleGoogleTranslate,
      mediaInfo: { isDesktop },
    } = this.props;

    const getHidden = () => {
      if (isElementary) {
        return isDesktop ? false : !showGoogleTranslate;
      }
      return !showGoogleTranslate;
    };
    return (
      <GoogleTranslate
        key="google-translate"
        hidden={getHidden()}
        isElementary={isElementary}
        onClose={() => toggleGoogleTranslate(false)}
      />
    );
  };

  renderMemberButton = () => {
    const {
      appBarPageDetails,
      user: { type },
    } = this.props;

    const { memberButtonHref, loginRedirect, hideSignUp } = appBarPageDetails;

    // AccountSetupScreen checks query params for redirecting after creating account - can use memberButtonHref to pass params
    const href = memberButtonHref || ONBOARDING;
    const isOrgGuest = type === USER_TYPES.ORGANIZATION_GUEST;

    const onLoginClick = () => {
      if (isOrgGuest) {
        this.clearGuestOrganizationCache();
      } else if (loginRedirect) {
        localStorage.setItem('loginRedirectURL', loginRedirect);
      }
    };

    const onSignUpClick = () => {
      if (isOrgGuest) {
        this.clearGuestOrganizationCache();
      }
      if (loginRedirect) {
        localStorage.setItem('loginRedirectURL', loginRedirect);
      }
    };

    return type === 'guest' || isOrgGuest ? (
      <Flexbox key="member-button" gap={2} marginLeft="auto">
        <Button
          color="default"
          onClick={onLoginClick}
          href={isOrgGuest ? ROOT : LOGIN_K12}
          variant="outlined"
          label="Log in"
        />
        {!hideSignUp && (
          <Button
            color="default"
            onClick={onSignUpClick}
            href={href}
            variant="filled"
            label="Sign up"
          />
        )}
      </Flexbox>
    ) : null;
  };

  renderPageDetails = () => {
    const { appBarPageDetails } = this.props;
    const { title, modal, module, classes, hasFeatureOnboarding } =
      appBarPageDetails;

    return (
      <PageDetailsContainer
        key="page-details"
        title={title}
        module={module}
        modal={modal}
        classes={classes}
        hasFeatureOnboarding={hasFeatureOnboarding}
      />
    );
  };

  renderDashboardSwitcher = () => {
    const {
      isOnboarding,
      router: { location },
    } = this.props;

    if (isOnboarding) return null;

    return (
      <K12DashboardSwitcher
        key="k12-dashboard-switcher"
        isCurrentlyElementaryPage={isElementaryPage(location.pathname)}
      />
    );
  };

  renderMessage = item => {
    const { isOnboarding } = this.props;
    if (isOnboarding) return null;

    return <Messages disabled={!item.isEnabled} />;
  };

  renderNotification = (item, onClick) => {
    const { isOnboarding } = this.props;
    if (isOnboarding) return null;

    return <Notifications onClick={onClick} />;
  };

  renderSettings = (item, onClick) => {
    const {
      showGoogleTranslate,
      toggleGoogleTranslate,
      moduleAccess,
      user,
      isOnboarding,
    } = this.props;

    return (
      <Settings
        showGoogleTranslate={showGoogleTranslate}
        toggleGoogleTranslate={toggleGoogleTranslate}
        moduleAccess={moduleAccess}
        isOnboarding={isOnboarding}
        user={user}
        onClick={onClick}
      />
    );
  };

  renderTextToSpeech = () => {
    const { color } = this.props;
    return <TextToSpeech key="test-to-speech" color={color} />;
  };

  renderLogoutButton = () => {
    const {
      mediaInfo,
      hideModal,
      showGoogleTranslate,
      toggleGoogleTranslate,
      router: { navigate },
    } = this.props;
    const { isDesktop } = mediaInfo;
    const { elementaryMobileMenuAnchor } = this.state;
    if (isDesktop) {
      return <LogoutButton key="logout-button" />;
    }

    return (
      <span key="logout-button">
        {this.renderGoogleTranslate({ isElementary: true })}
        <IconButton
          className={s.moreIcon}
          color="inherit"
          onClick={({ currentTarget }) =>
            this.setState({ elementaryMobileMenuAnchor: currentTarget })
          }
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </IconButton>
        <Popover
          anchorEl={elementaryMobileMenuAnchor}
          open={Boolean(elementaryMobileMenuAnchor)}
          onClose={() => this.setState({ elementaryMobileMenuAnchor: null })}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <LanguageTranslationMenuItem
            showIcon
            className={s.menuItem}
            showGoogleTranslate={showGoogleTranslate}
            onClickCallback={() => {
              toggleGoogleTranslate(!showGoogleTranslate);
              this.setState({ elementaryMobileMenuAnchor: null });
            }}
          />
          <ClickableComponent className={cn('intercom-launcher', s.menuItem)}>
            <Chat />
            Chat Support
          </ClickableComponent>
          <ClickableComponent
            className={s.menuItem}
            onClick={() => {
              hideModal();
              navigate(LOGOUT);
            }}
          >
            <LogoutIcon />
            Logout
          </ClickableComponent>
        </Popover>
      </span>
    );
  };

  renderBackButton = () => {
    const { t } = this.props;

    const handleBackButtonClick = event => {
      const {
        router: { navigate, location },
        hideModal,
        isModalVisible,
      } = this.props;
      event.preventDefault();

      if (isModalVisible) {
        return hideModal();
      }

      if (location.pathname === EL_STUDENT_CAREER_QUIZ) {
        return navigate(EL_STUDENT_DASHBOARD);
      }
      return navigate(-1);
    };

    return (
      <Button
        key="back-button"
        hasWhiteBackground={false}
        className={s.backBtn}
        variant="outlined"
        color="secondary"
        hasIconLeft
        icon={<FontAwesomeIcon icon={faArrowLeft} />}
        tooltipProps={{
          title: t('Common.GoBack'),
          placement: 'bottom',
        }}
        aria-label={t('Common.GoBack')}
        onClick={handleBackButtonClick}
      />
    );
  };

  render() {
    const {
      featureOnboarding,
      router: { location },
      appBar,
      showBackButton,
      appBarPageDetails,
      isOnboarding,
      ...restProps
    } = this.props;
    const { left: leftKeys = [], right: rightKeys = [] } = { ...appBar };

    const feature = find(featureOnboarding, o =>
      location.pathname.includes(o.feUrl),
    );

    const sortItemsByOrder = (items, orderArray) =>
      items.sort((a, b) => {
        const indexA = orderArray.indexOf(a.key);
        const indexB = orderArray.indexOf(b.key);

        // If item is not in order array, put it at the end
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });

    const getAppBarItem = item => {
      const renderFunction = {
        'app-bar-avatar': this.renderAvatar,
        'app-bar-credential-wallet': this.renderCredentialWalletButton,
        'app-bar-demo-student-button': this.renderDemoStudentButton,
        'app-bar-documents': this.renderDocumentsButton,
        'app-bar-edit-profile-avatar': thisItem =>
          this.renderAvatar({ withName: true, editProfile: true }, thisItem),
        'app-bar-google-translate': this.renderGoogleTranslate,
        'app-bar-google-translate-elementary': () =>
          this.renderGoogleTranslate({ isElementary: true }),
        'app-bar-member': this.renderMemberButton,
        'app-bar-page-details': this.renderPageDetails,
        'app-bar-k12-admin-dashboard-switcher': this.renderDashboardSwitcher,
        'app-bar-message': this.renderMessage,
        'app-bar-notification': this.renderNotification,
        'app-bar-settings': this.renderSettings,
        'app-bar-welcome-widget': this.renderWelcomeWidget,
        'app-bar-schoolinks': null, // TODO: Remove from BE
        'app-bar-speaker': this.renderTextToSpeech,
        'app-bar-logout': this.renderLogoutButton,
        'app-bar-back-button': showBackButton ? this.renderBackButton : null,
      }[item.key];

      const isManagedTooltip = [
        'app-bar-demo-student-button',
        'app-bar-notification',
        'app-bar-settings',
        'app-bar-welcome-widget',
      ].includes(item.key);

      if (typeof renderFunction === 'function') {
        return item?.tooltip && !isManagedTooltip && !isOnboarding ? (
          <Item item={item} renderFunction={renderFunction} />
        ) : (
          renderFunction(item)
        );
      }
      return null;
    };

    const conditionalRightKeys =
      feature && !rightKeys.map(e => e.key).includes('app-bar-welcome-widget')
        ? ['app-bar-welcome-widget', ...rightKeys]
        : rightKeys;

    const rightKeysArray = Array.isArray(conditionalRightKeys)
      ? conditionalRightKeys
      : [conditionalRightKeys];

    window.rightKeysArray = rightKeysArray;

    const sortedLeftKeys = sortItemsByOrder([...leftKeys], APP_BAR_ITEM_ORDER);
    const sortedRightKeys = sortItemsByOrder(
      [...rightKeysArray],
      APP_BAR_ITEM_ORDER,
    );

    const [firstItem, ...restItems] = sortedLeftKeys;
    const publicMemberPage = appBarPageDetails?.isPublicMemberPage
      ? [{ key: 'app-bar-home-name' }, { key: 'app-bar-member' }]
      : null;
    const tempLeftKeys = [
      firstItem,
      ...ensureArray(publicMemberPage),
      ...restItems,
    ].filter(item => item);

    const pageDetails =
      !isEmpty(appBarPageDetails) && !appBarPageDetails?.isPublicMemberPage
        ? getAppBarItem({ key: 'app-bar-page-details' })
        : null;

    return (
      <>
        <Wrapper
          {...restProps}
          pageDetails={pageDetails}
          items={[
            ...tempLeftKeys,
            { key: 'app-bar-back-button' },
            { key: 'app-bar-k12-admin-dashboard-switcher' },
            ...sortedRightKeys,
          ]
            .map(item => getAppBarItem(item))
            .filter(Boolean)}
          isPublicMemberPage={appBarPageDetails?.isPublicMemberPage}
        />
      </>
    );
  }
}

DesktopAppBarV2.propTypes = {
  ...ModalPropTypes,
  user: shape({}),
  isOnboarding: bool,
  profilePic: string,
  firstName: string,
  lastName: string,
  preferredName: string,
  showGoogleTranslate: bool,
  appBarPageDetails: shape({
    isPublicMemberPage: bool,
    memberButtonHref: string,
    loginRedirect: shape({}),
    hideSignUp: bool,
  }),
  toggleGoogleTranslate: func,
  moduleAccess: shape({}),
  mediaInfo: shape({}).isRequired,
  router: RouterPropTypes,
  isModalVisible: bool,
  shouldRenderAffiliationLink: bool,
  featureOnboarding: arrayOf(shape({})),
  appBar: shape({}),
  showBackButton: bool,
};

DesktopAppBarV2.defaultProps = {
  ...ModalDefaultProps,
  isOnboarding: false,
  preferredName: null,
  /**
   * @param isPublicMemberPage - If true, show app-bar-member
   * @param memberButtonHref - Url for onboarding when user presses sign up
   * @param loginRedirect - Redirects users after logging in (LoginRedirect). Needs to be in correct object shape for hooks/useRedirect
   * @param hideSignUp - If true, hides sign up button
   */
  appBarPageDetails: {
    isPublicMemberPage: false,
    memberButtonHref: null,
    loginRedirect: null,
    hideSignUp: false,
  },
  router: null,
  user: {},
  profilePic: null,
  firstName: null,
  lastName: null,
  showGoogleTranslate: false,
  toggleGoogleTranslate: noop,
  moduleAccess: null,
  isModalVisible: false,
  shouldRenderAffiliationLink: false,
  featureOnboarding: [],
  appBar: null,
  showBackButton: false,
};

export default DesktopAppBarV2;
